import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import Loader from '../../../components/Loader';
import Error from '../../../components/Error';
import { PENDING_CHECKOUT_KEY } from '../../../components/DeepLinkHandler';
import {
    useBookingWizard,
    getSelectedBundles,
    computeTotal,
    getFirstIncompleteStep,
    participantsToPeoplePayload
} from './BookingWizardContext';
import { getBundleLabel } from '../../../utils/getBundleLabel';

// Step 4 — Riepilogo prenotazione. Matite di modifica solo per data e dati partecipanti
// (riportano al rispettivo step). "Conferma prenotazione" invia la richiesta al server:
// flusso standard → schermata di successo; auto-accept (esperienze proprie TicinoWild) →
// redirect immediato al checkout Stripe (stessa logica della vecchia SendRequest).

const formatSummaryDate = (isoDate, language) => {
    if (!isoDate) return null;
    const date = new Date(`${isoDate}T00:00:00`);
    if (Number.isNaN(date.getTime())) return isoDate;
    return date.toLocaleDateString(language, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
};

const formatBirthdate = (isoDate) => {
    const parts = (isoDate || '').split('-');
    return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : isoDate;
};

const StepSummary = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { activityData, booking, resetBooking } = useBookingWizard();
    const pickerId = useSelector(state => state.user.id);

    const [sendStatus, setSendStatus] = useState('idle');
    const [sendError, setSendError] = useState(null);

    const selectedBundles = getSelectedBundles(activityData, booking);
    const total = computeTotal(activityData, booking);

    // Guardia: si arriva al riepilogo solo con tutti gli step completi
    const firstIncomplete = getFirstIncompleteStep(activityData, booking);
    if (firstIncomplete !== 'riepilogo') {
        return <Navigate to={`../${firstIncomplete}`} replace />;
    }

    const selectedSlot = activityData.bookingType === 'fixedSlots'
        ? (activityData.slots || []).find(element => element._id === booking.slotId)
        : null;
    // Una riga per opzione selezionata; con più opzioni combinate (solo perPerson)
    // la quantità è parte dell'etichetta ("2 × 50 CHF / persona")
    const bundleLines = selectedBundles.map(({ bundle, quantity }) => {
        const { label } = getBundleLabel(bundle, t);
        return {
            key: bundle._id,
            text: selectedBundles.length > 1 ? `${quantity} × ${label}${bundle.note ? ` (${bundle.note})` : ''}` : label
        };
    });

    const sendRequest = async () => {
        setSendStatus('loading');
        const token = localStorage.getItem('ticino-wild-token');
        const url = `${process.env.REACT_APP_SERVER_BASE_URL}/requests/create-new-request`;
        const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
        // Selezione singola: payload storico (bundleId + quantity). Più opzioni combinate:
        // SOLO bundleSelections — un server non ancora aggiornato risponde errore ("Pacchetto
        // non trovato") invece di prezzare male la richiesta.
        const isMultiBundle = booking.selections.length > 1;
        const body = JSON.stringify({
            activityId: activityData._id,
            pickerId,
            companyId: activityData.company,
            bundleId: isMultiBundle ? undefined : booking.selections[0]?.bundleId,
            quantity: isMultiBundle ? undefined : booking.selections[0]?.quantity,
            bundleSelections: isMultiBundle ? booking.selections : undefined,
            slotId: booking.slotId,
            preferredDate: booking.preferredDate,
            preferredTime: booking.preferredTime,
            alternativeSlots: booking.alternativeSlots.trim() || null,
            observations: booking.observations.trim() || null,
            customFieldAnswers: Object.entries(booking.customFieldAnswers)
                .filter(([, answer]) => answer && answer.trim() !== '')
                .map(([label, answer]) => ({ label, answer: answer.trim() })),
            people: participantsToPeoplePayload(booking.participants)
        });
        try {
            const response = await fetch(url, { method: 'POST', headers, body });
            if (response.ok) {
                const result = await response.json();
                // Auto-accept (esperienza propria TicinoWild): il backend restituisce l'URL di pagamento
                // → reindirizzo subito il picker al checkout, saltando il passaggio dal profilo.
                if (result.url) {
                    resetBooking({ sent: true });
                    if (Capacitor.isNativePlatform()) {
                        localStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify({
                            type: 'activity_request',
                            requestId: result.payload._id,
                            sessionId: result.sessionId
                        }));
                        await Browser.open({ url: result.url });
                    } else {
                        window.location.href = result.url;
                    }
                    return;
                }
                // Flusso standard: schermata di successo
                resetBooking({ sent: true });
                navigate('../inviata', { replace: true });
            } else {
                const error = await response.json();
                throw new Error(error.message || "Errore nell'invio della richiesta.");
            }
        } catch (error) {
            console.error(error);
            setSendError(error);
            setSendStatus('error');
        }
    };

    return (
        <div className="w-full flex flex-col gap-6 items-center">
            <div className="text-center flex flex-col gap-2">
                <h2>{t('BookingWizard.summary.title')}</h2>
                <p className="text-neutral-500">{t('BookingWizard.summary.intro')}</p>
            </div>

            {/* Esperienza + slot (matita → step data) */}
            <div className="w-full p-4 border-2 rounded-xl bg-white flex justify-between gap-2">
                <div className="flex flex-col gap-1">
                    <div className="font-bold">{activityData.title}</div>
                    {
                        booking.preferredDate &&
                        <div className="capitalize">
                            {formatSummaryDate(booking.preferredDate, i18n.language)}
                            {booking.preferredTime && <span> · {booking.preferredTime}{selectedSlot?.endTime ? ` – ${selectedSlot.endTime}` : ''}</span>}
                        </div>
                    }
                    {
                        bundleLines.map(line => (
                            <div key={line.key} className="text-neutral-600">{line.text}</div>
                        ))
                    }
                </div>
                <button aria-label={t('BookingWizard.summary.edit')} onClick={() => navigate('../data')}>
                    <i className="fi fi-rr-pencil text-[#225424]"></i>
                </button>
            </div>

            {/* Partecipanti (matita → step partecipanti) */}
            <div className="w-full p-4 border-2 rounded-xl bg-white flex justify-between gap-2">
                <div className="flex flex-col gap-2">
                    <div className="font-bold">{t('BookingWizard.summary.participants')}</div>
                    {
                        booking.participants.map((participant, index) => (
                            <div key={index}>
                                {index === 0 ? `${participant.name} ${participant.surname}` : t('BookingWizard.participants.participantN', { n: index + 1 })}
                                <span className="text-neutral-500"> · {formatBirthdate(participant.birthdate)}</span>
                            </div>
                        ))
                    }
                    {
                        Object.entries(booking.customFieldAnswers)
                            .filter(([, answer]) => answer && answer.trim() !== '')
                            .map(([label, answer]) => (
                                <div key={label} className="text-sm text-neutral-600">{label}: {answer}</div>
                            ))
                    }
                    {booking.observations.trim() !== '' && <div className="text-sm text-neutral-600 italic">{booking.observations}</div>}
                </div>
                <button aria-label={t('BookingWizard.summary.edit')} onClick={() => navigate('../partecipanti')}>
                    <i className="fi fi-rr-pencil text-[#225424]"></i>
                </button>
            </div>

            {/* Totale */}
            {
                total != null &&
                <div className="w-full flex justify-between items-center px-2">
                    <div className="font-bold">{t('BookingWizard.summary.total')}</div>
                    <div className="font-bold text-lg">{total} CHF</div>
                </div>
            }

            <div className="w-full p-3 rounded-xl bg-green-50 border border-[#225424] text-sm text-[#225424]">
                {t('BookingWizard.summary.confirmNote')}
            </div>

            {
                sendStatus === 'idle' &&
                <button className="bg-[#225424] py-3 px-6 text-white rounded-xl w-full md:w-fit" onClick={sendRequest}>
                    {t('BookingWizard.summary.confirm')}
                </button>
            }
            {sendStatus === 'loading' && <Loader />}
            {
                sendStatus === 'error' &&
                <div className="w-full flex flex-col items-center gap-3">
                    <Error message={sendError?.message} />
                    <button className="bg-[#225424] py-3 px-6 text-white rounded-xl w-full md:w-fit" onClick={() => { setSendStatus('idle'); setSendError(null); }}>
                        {t('BookingWizard.summary.retry')}
                    </button>
                </div>
            }
        </div>
    );
};

export default StepSummary;
